/**
 * Flattens the component registry into a JSON shape the AI agent can
 * consume to know what props each component accepts and which enum
 * values are valid. Sent with every POST /ai/v1/chat call — which requires
 * `registry_spec` to be a LIST (array) of component specs — so the model
 * stops hallucinating raw values for token-typed props.
 */

import { registry } from "@dynamic-landing/components";

import { describeSchema, type FieldDescriptor, type SchemaLike } from "./schemaIntrospect";

type FieldSpec = {
  name: string;
  required: boolean;
  type: "string" | "number" | "boolean" | "enum" | "json";
  values?: string[];
  default?: unknown;
};

export type ComponentSpec = {
  name: string;
  acceptsChildren: boolean;
  fields: FieldSpec[];
};

/** The agent expects a LIST of component specs. */
export type RegistrySpec = ComponentSpec[];

let cached: RegistrySpec | null = null;

export function getRegistrySpec(): RegistrySpec {
  if (cached) return cached;
  const out: RegistrySpec = [];
  for (const [name, entry] of Object.entries(registry)) {
    const fields: FieldSpec[] = describeSchema(
      entry.schema as unknown as SchemaLike,
    ).map(toFieldSpec);
    out.push({
      name,
      acceptsChildren: !!entry.acceptsChildren,
      fields,
    });
  }
  cached = out;
  return out;
}

function toFieldSpec(d: FieldDescriptor): FieldSpec {
  const base: FieldSpec = {
    name: d.name,
    required: d.required,
    type:
      d.kind.kind === "string"
        ? "string"
        : d.kind.kind === "number"
          ? "number"
          : d.kind.kind === "boolean"
            ? "boolean"
            : d.kind.kind === "enum"
              ? "enum"
              : "json",
  };
  if (d.kind.kind === "enum") base.values = d.kind.options;
  if (d.defaultValue !== undefined) base.default = d.defaultValue;
  return base;
}
